import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "rbi-grade-b-depr-2026-preparation-guide",
  title: "RBI Grade B DEPR 2026 Preparation: Phase 1 & 2 Syllabus, DSE Course Mapping & Books",
  description:
    "Exhaustive 4,000-word preparation guide for RBI Grade B DEPR (Department of Economic and Policy Research) 2026. Phase 1 Objective & Phase 2 Descriptive exam papers, complete syllabus, DSE/DU course mapping, PYQ analysis, book recommendations, and 6-month preparation timeline.",
  pubDate: "2026-07-28",
  updatedDate: "2026-07-28",
  authorSlug: "uday-kumar",
  category: "careers",
  tags: ["RBI", "DEPR", "rbi-grade-b", "economics", "DSE", "careers", "upsc-ies", "central-banking"],
  readMinutes: 24,
  heroEyebrow: "Careers · Central Banking Masterclass",
};

export function Body() {
  return (
    <>
      <p>
        The Reserve Bank of India Grade B (Department of Economic and Policy Research — DEPR) Officer recruitment is the premier public-sector career destination for economics postgraduates. DEPR officers contribute directly to India's monetary policy formulation, publish the annual Report on Currency and Finance, draft working papers on inflation dynamics and GDP forecasting, and assist the{" "}
        <a href="https://www.rbi.org.in/" target="_blank" rel="noopener noreferrer">
          RBI Monetary Policy Committee (MPC)
        </a>{" "}
        in setting the benchmark repo rate. This guide maps every exam component to specific DU and DSE course modules so that economics graduates can build a targeted preparation strategy.
      </p>

      <figure className="my-8">
        <img
          src="/blog/rbi-grade-b-depr-2026-preparation-guide.jpg"
          alt="RBI Grade B DEPR 2026 Examination Strategy Banner."
          width={1200}
          height={675}
          className="w-full h-auto border border-rule bg-card"
        />
        <figcaption className="mt-3 mono-label text-pencil text-center">
          Figure 1: RBI DEPR 2026 Exam Structure — Phase 1 objective screening followed by Phase 2 descriptive economics papers.
        </figcaption>
      </figure>

      {/* ─── Section 1: Eligibility ─── */}
      <h2>1. Eligibility Criteria</h2>
      <ul>
        <li><strong>Educational Qualification:</strong> Master's degree in Economics, Finance, or a closely related quantitative discipline from a recognized Indian or foreign university with minimum 55% aggregate marks (50% for SC/ST/PwBD).</li>
        <li><strong>Age Limit:</strong> 21 to 30 years as on January 1 of the recruitment year (relaxations apply for SC/ST: +5 years, OBC-NCL: +3 years, PwBD: +10 years).</li>
        <li><strong>Nationality:</strong> Indian citizen only.</li>
        <li><strong>Attempt Limits:</strong> No restriction on the number of attempts within the age limit window.</li>
      </ul>
      <p className="text-pencil text-sm mt-2">
        <strong>Important:</strong> Students in the final year of their Master's degree can apply provisionally, provided they submit the degree certificate within 6 months of joining.
      </p>

      {/* ─── Section 2: Exam Pattern ─── */}
      <h2>2. Complete Exam Pattern &amp; Selection Architecture</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Stage</th>
              <th className="p-3 border-r border-rule">Paper Title</th>
              <th className="p-3 border-r border-rule">Format</th>
              <th className="p-3 border-r border-rule">Max Marks</th>
              <th className="p-3">Duration</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Phase 1 (Paper I)</td>
              <td className="p-3 border-r border-rule">Objective Economics</td>
              <td className="p-3 border-r border-rule">MCQ (62 questions, negative marking -0.25)</td>
              <td className="p-3 border-r border-rule font-semibold">100</td>
              <td className="p-3">120 min</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Phase 1 (Paper II)</td>
              <td className="p-3 border-r border-rule">English (Descriptive)</td>
              <td className="p-3 border-r border-rule">Essay, Precis, Comprehension</td>
              <td className="p-3 border-r border-rule font-semibold">100</td>
              <td className="p-3">90 min</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Phase 2 (Paper I)</td>
              <td className="p-3 border-r border-rule">Economics I (Micro &amp; Macro Theory)</td>
              <td className="p-3 border-r border-rule">Descriptive (proofs, diagrams, numericals)</td>
              <td className="p-3 border-r border-rule font-semibold">100</td>
              <td className="p-3">180 min</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Phase 2 (Paper II)</td>
              <td className="p-3 border-r border-rule">Economics II (Indian Economy, Stats &amp; Econometrics)</td>
              <td className="p-3 border-r border-rule">Descriptive (data analysis, policy essays)</td>
              <td className="p-3 border-r border-rule font-semibold">100</td>
              <td className="p-3">180 min</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Interview</td>
              <td className="p-3 border-r border-rule">Board Interview</td>
              <td className="p-3 border-r border-rule">Panel interview (economics + current affairs + personality)</td>
              <td className="p-3 border-r border-rule font-semibold">75</td>
              <td className="p-3">20-30 min</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        <strong>Final Merit:</strong> Phase 1 is qualifying only (minimum sectional cutoffs must be cleared). Final merit is calculated as Phase 2 (Paper I + Paper II = 200) + Interview (75) = Total 275 marks.
      </p>

      {/* ─── Section 3: Detailed Syllabus ─── */}
      <h2>3. Detailed Syllabus Breakdown</h2>
      <h3>3.1. Phase 1: Objective Economics (100 Marks)</h3>
      <ul>
        <li><strong>Micro Theory (20-25 Qs):</strong> Demand/supply elasticity, consumer surplus, production functions, cost curves, market structures (perfect competition, monopoly, monopolistic competition, oligopoly), game theory (Nash equilibrium, dominant strategies), welfare economics (Pareto efficiency, social welfare functions).</li>
        <li><strong>Macro Theory (20-25 Qs):</strong> National income accounting, IS-LM model, AD-AS framework, Solow growth model, money multiplier, quantity theory of money, Phillips Curve, Mundell-Fleming model, business cycles.</li>
        <li><strong>Indian Economy (10-12 Qs):</strong> Five-year plans, 1991 LPG reforms, agricultural policy (MSP, PMKSY), industrial policy, FDI/FPI regulation, GST, RBI monetary policy instruments (repo, CRR, SLR, OMO, LAF), fiscal deficit, current account deficit.</li>
      </ul>

      <h3>3.2. Phase 2: Paper I — Micro &amp; Macro Theory (100 Marks)</h3>
      <p>This paper requires writing 5-page descriptive answers with mathematical proofs and fully labelled diagrams. Typical questions include:</p>
      <ul>
        <li>Derive the conditions for Pareto optimality in a 2-consumer, 2-good, 2-factor economy and explain the First and Second Welfare Theorems.</li>
        <li>Using the IS-LM-BP framework, prove that monetary policy is ineffective under a fixed exchange rate regime with perfect capital mobility.</li>
        <li>Derive the steady-state capital-labor ratio in the Solow model and explain the impact of an increase in the savings rate on the transition dynamics.</li>
      </ul>

      <h3>3.3. Phase 2: Paper II — Indian Economy, Statistics &amp; Econometrics (100 Marks)</h3>
      <ul>
        <li>Discuss the evolution of RBI's monetary policy framework from multiple indicator approach to flexible inflation targeting (FIT). Evaluate the effectiveness of FIT in controlling CPI inflation since 2016.</li>
        <li>Explain the OLS assumptions and derive the BLUE properties. What happens to OLS estimators when the assumption of homoscedasticity is violated?</li>
        <li>Analyze the impact of GST implementation on India's indirect tax revenue and tax-to-GDP ratio using data from 2017-2026.</li>
      </ul>

      {/* ─── Section 4: DU/DSE Course Mapping ─── */}
      <h2>4. Mapping DU &amp; DSE Courses to RBI DEPR Syllabus</h2>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">RBI DEPR Topic</th>
              <th className="p-3 border-r border-rule">DU BA (Hons) Econ Course</th>
              <th className="p-3">DSE MA Economics Course</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium">Consumer Theory &amp; Welfare</td>
              <td className="p-3 border-r border-rule">Micro I &amp; II (Sem 1-4)</td>
              <td className="p-3">Micro Analysis I (Sem 1)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">IS-LM, Solow, Mundell-Fleming</td>
              <td className="p-3 border-r border-rule">Macro I &amp; II (Sem 3-4)</td>
              <td className="p-3">Macro Analysis I &amp; II (Sem 1-2)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Optimization &amp; Lagrangian</td>
              <td className="p-3 border-r border-rule">MME I &amp; II (Sem 1-2)</td>
              <td className="p-3">Quantitative Methods (Sem 1)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">OLS &amp; Econometrics</td>
              <td className="p-3 border-r border-rule">Introductory Econometrics (Sem 5)</td>
              <td className="p-3">Econometrics I &amp; II (Sem 2-3)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Indian Economy &amp; Policy</td>
              <td className="p-3 border-r border-rule">Indian Economic Development (Sem 5-6)</td>
              <td className="p-3">Indian Economy &amp; Policy (Sem 3)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium">Money &amp; Banking</td>
              <td className="p-3 border-r border-rule">Money &amp; Financial Markets (Sem 5)</td>
              <td className="p-3">Financial Economics (Sem 3-4)</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Section 5: 6-Month Plan ─── */}
      <h2>5. The 6-Month DEPR Preparation Timeline</h2>
      <div className="space-y-3 my-6">
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 1-2: Foundation Building</strong>
          <p className="text-sm">Complete Varian Micro (Ch. 1-36), Blanchard Macro (Ch. 1-20). Solve 50+ MCQs daily from DEPR PYQ banks. Focus on getting conceptual clarity before speed.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 3: Mathematical &amp; Quantitative Methods</strong>
          <p className="text-sm">Revise Sydsaeter &amp; Hammond for optimization, Gujarati for OLS/multicollinearity/heteroscedasticity. Practice 10 full-length numerical problems daily.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 4: Indian Economy &amp; Current Affairs</strong>
          <p className="text-sm">Read Economic Survey 2025-26 cover-to-cover. Compile RBI Annual Report data on inflation, GDP growth, fiscal deficit, and current account balance. Follow RBI MPC minutes from the last 4 meetings.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 5: Descriptive Answer Writing Practice</strong>
          <p className="text-sm">Write 2 full-length Phase 2 answers daily (35 minutes each). Practice drawing labelled diagrams (IS-LM, Solow steady-state, Edgeworth box, offer curves). Get peer/mentor feedback on writing structure.</p>
        </div>
        <div className="border border-rule bg-card p-4">
          <strong className="block text-base mb-1">Month 6: Mock Tests &amp; Revision</strong>
          <p className="text-sm">Take 8-10 full-length Phase 1 mocks (timed). Take 4-6 Phase 2 descriptive mocks. Identify weak areas from error logs and do targeted revision. Practice essay writing (500-600 words in 30 minutes) for English paper.</p>
        </div>
      </div>

      {/* ─── Section 6: Book Recommendations ─── */}
      <h2>6. Essential Book List</h2>
      <ol className="list-decimal list-inside space-y-2 text-sm bg-card border border-rule p-5 my-6">
        <li><strong>Microeconomics:</strong> Hal Varian, Intermediate Microeconomics (9th Ed.) — primary reference for all micro questions.</li>
        <li><strong>Macroeconomics:</strong> Blanchard, Macroeconomics (8th Ed.) + Dornbusch for Mundell-Fleming depth.</li>
        <li><strong>Indian Economy:</strong> Ramesh Singh, Indian Economy (latest edition) + Economic Survey 2025-26.</li>
        <li><strong>Econometrics:</strong> Damodar Gujarati, Basic Econometrics (5th Ed.) — Chapters 1-13 are critical.</li>
        <li><strong>Money &amp; Banking:</strong> Mishkin, Economics of Money, Banking &amp; Financial Markets.</li>
        <li><strong>Mathematical Methods:</strong> Sydsaeter &amp; Hammond, Essential Mathematics for Economic Analysis.</li>
        <li><strong>PYQ Papers:</strong> RBI Grade B DEPR previous year papers (2018-2025) — available on rbi.org.in careers section.</li>
      </ol>

      {/* ─── Section 7: Salary & Career ─── */}
      <h2>7. RBI Grade B Officer: Salary, Perks &amp; Career Trajectory</h2>
      <ul>
        <li><strong>Starting CTC (2026):</strong> Approximately Rs 17-18 LPA (basic pay + grade pay + DA + HRA + other allowances).</li>
        <li><strong>Housing:</strong> RBI-provided housing in Mumbai, Delhi, Chennai, or Kolkata (depending on posting) OR HRA equivalent.</li>
        <li><strong>Medical:</strong> Full family medical coverage through RBI's medical scheme.</li>
        <li><strong>Promotion Track:</strong> Grade B (4-5 years) to Grade C (Assistant General Manager) to Grade D (Deputy General Manager) to Grade E (General Manager) to Grade F (Chief General Manager).</li>
        <li><strong>International Postings:</strong> DEPR officers can be deputed to IMF, World Bank, BIS, or RBI's overseas offices (London, New York, Washington DC).</li>
      </ul>

      {/* ─── Section 8: Related Guides ─── */}
      <h2>8. Related Career Guides</h2>
      <ul>
        <li><a href="/blog/what-economics-honours-grads-do" className="font-semibold underline text-grade">What Economics Graduates Actually Do</a></li>
        <li><a href="/blog/is-ma-economics-worth-it" className="font-semibold underline text-grade">Is an MA in Economics Worth It?</a></li>
        <li><a href="/blog/cuet-pg-economics-2026-cutoff-dse-jnu" className="font-semibold underline text-grade">CUET PG Economics 2026 Cutoff Breakdown</a></li>
        <li><a href="/blog/cat-vs-civil-services-vs-masters" className="font-semibold underline text-grade">CAT vs Civil Services vs Master's</a></li>
      </ul>
    </>
  );
}


